import { guestChoosesShop } from '@croissants/contracts/projections';
import { useEffect } from 'react';
import { soonestMorning } from '../lib/stay';
import { useBasket } from '../state/basket-provider';
import { useCheckoutDraft } from '../state/checkout';
import { useSession } from '../state/session';
import { useWindows } from './queries';

/**
 * The picker step, without the picker (PRD GST-042, RTE-034).
 *
 * With immediate delivery on and pre-order off there is exactly one morning -
 * today - and exactly one window a guest ordering for now wants: the soonest
 * one the platform can still reach. A screen asking which would be a checkpoint
 * in front of a question with one answer, so the scan opens the menu
 * (`entryStep`) and this settles the draft behind it.
 *
 * It is settled CLIENT-SIDE, from the picker's own answer, rather than left for
 * checkout to choose server-side, and that is the decision worth naming. Every
 * screen after the scan is written against a concrete window: the menu states
 * the arrival range, the quote is asked for a start instant, the review lists
 * it, the conflict panel replaces it. A draft carrying "today, whichever window
 * you like" would leave all of them with a hole to design around and the guest
 * with no arrival promise until after they had paid - which is the one thing
 * the immediate journey is supposed to give them first.
 *
 * The read costs nothing where it matters: `useWindows(basket, null)` is the
 * exact key the picker and `ChosenMorning` already ask under, so on the menu,
 * the basket and the review this is a cache read rather than a request.
 *
 * `soonest` is the server's tag and never a position in the list - the first
 * row is only the soonest window when the server says it is - and the fallback
 * is the first SELECTABLE window, never a greyed one.
 *
 * It writes once and stops: the query is disabled the moment the draft holds a
 * window, so a guest who opened "Change" and chose a later time this afternoon
 * is not quietly moved back to the soonest one.
 */
export function useImmediateWindow(): void {
  const { session } = useSession();
  const { basket } = useBasket();
  const { draft, update } = useCheckoutDraft();

  const modes = session?.orderingModes;
  /**
   * Mode B settles nothing until the house is chosen (GST-030).
   *
   * Asked with no `shopId`, the window list is the UNION across every eligible
   * counter, and the soonest window in that union is not necessarily one the
   * house the guest then picks can serve - so settling it early would hand the
   * review step a window checkout refuses, for a choice the guest never made.
   * Mode A has no house to wait for and settles on the scan.
   */
  /* Mode B2 settles on the scan like mode A: the platform has already chosen
     the shop, so there is no house to wait for (founder, 2026-09-20). */
  const houseSettled =
    session === undefined ||
    session === null ||
    !guestChoosesShop(session.storefrontMode) ||
    basket.shopId !== null;
  const immediateOnly = modes?.immediate === true && !modes.preorder && houseSettled;
  /**
   * The one morning that can be ordered, from the session's own answer
   * (GST-002) - which is today whenever immediate delivery is on.
   *
   * A draft holding a LATER morning is a leftover from when pre-order was on,
   * and `withoutPastMorning` cannot drop it: that rule only drops mornings
   * already behind us. Left alone it would price and place a pre-order the
   * platform is no longer selling, and the guest would meet `ordering_closed`
   * at the payment sheet. Read from a fact this app already has, so noticing it
   * costs no request.
   */
  const earliest = session?.nextServiceDay ?? null;
  const needsWindow =
    draft.serviceDay === null ||
    draft.windowStart === null ||
    (earliest !== null && draft.serviceDay !== earliest);

  const query = useWindows(basket, null, immediateOnly && needsWindow);
  const answer = query.data;

  useEffect(() => {
    if (!immediateOnly || !needsWindow) return;
    // ONE derivation of "today, as soon as we can" (lib/stay.ts) - mode B2's
    // "Order now" button settles the draft from the same function.
    const morning = soonestMorning(answer?.serviceDays);
    if (morning === null) return;
    update({ ...morning, selectedDays: [...morning.selectedDays] });
  }, [immediateOnly, needsWindow, answer, update]);
}
